import { useState, useEffect } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../auth/authStore';
import { useIsAdmin } from '../../auth/permissions';
import { MessageSquarePlus, History, Settings, ChevronRight, ChevronLeft, ChevronDown, Trash2, X, ClipboardCheck, BarChart3, LogOut, Shield, FlaskConical, Network, SearchCheck, PanelLeftClose, PanelLeftOpen, Key, Sliders, GitBranch, FileSearch } from 'lucide-react';
import { SettingsModal } from '../settings/SettingsModal';

/** Hook to track current hash route for active-link highlighting. */
function useActiveRoute() {
  const [route, setRoute] = useState(window.location.hash || '#/');
  useEffect(() => {
    const h = () => setRoute(window.location.hash || '#/');
    window.addEventListener('hashchange', h);
    return () => window.removeEventListener('hashchange', h);
  }, []);
  return route;
}

/**
 * Navigation sidebar with chat session history, new-chat button,
 * admin links, ripgrep search toggle, settings, and sign-out.
 */
export function Sidebar() {
  const { sessions, sessionId, startNewSession, loadSession, clearAllSessions, deleteSession, sidebarCollapsed, setSidebarCollapsed } = useChatStore();
  const { profile, logout } = useAuthStore();
  const [hoveredSession, setHoveredSession] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [evalMenuOpen, setEvalMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [chatPage, setChatPage] = useState(0);
  const activeRoute = useActiveRoute();
  const collapsed = sidebarCollapsed;
  const isAdmin = useIsAdmin();

  const navLinkClass = (hash: string) =>
    `w-full flex items-center rounded-lg transition-colors text-sm ${
      collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-3 py-2'
    } ${
      activeRoute === hash
        ? 'bg-gray-700 text-white'
        : 'text-gray-400 hover:bg-gray-800'
    }`;

  const subNavLinkClass = (hash: string) =>
    `w-full flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-xs ${
      activeRoute === hash
        ? 'bg-gray-700 text-white'
        : 'text-gray-400 hover:bg-gray-800'
    }`;

  const CHATS_PER_PAGE = 10;
  const totalChatPages = Math.max(1, Math.ceil(sessions.length / CHATS_PER_PAGE));
  // Clamp chatPage if sessions shrink (e.g. after delete)
  const safePage = Math.min(chatPage, totalChatPages - 1);
  const paginatedSessions = sessions.slice(safePage * CHATS_PER_PAGE, (safePage + 1) * CHATS_PER_PAGE);

  return (
    <div className="h-full flex flex-col">
      {/* Header with collapse toggle */}
      <div className={`border-b border-gray-700 ${collapsed ? 'p-2' : 'p-4'} flex items-start ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <span className="text-primary-400">LI</span>SA
            </h1>
            <p className="text-xs text-gray-400 mt-1">Liner Support Assistant</p>
          </div>
        )}
        <button
          onClick={() => setSidebarCollapsed(!collapsed)}
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition-colors flex-shrink-0"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      {/* New Chat Button */}
      <div className={collapsed ? 'p-2' : 'p-3'}>
        <button
          onClick={() => {
            startNewSession();
            // Navigate to chat route if not already there — otherwise the new
            // session is created in the background but the user stays on the
            // current page (Issue Analysis, admin, etc.) and sees no change.
            if (window.location.hash !== '#/' && window.location.hash !== '#' && window.location.hash !== '') {
              window.location.hash = '#/';
            }
          }}
          className={`w-full flex items-center rounded-lg border border-gray-600 hover:bg-gray-800 transition-colors ${
            collapsed ? 'justify-center p-2' : 'gap-2 px-3 py-2'
          }`}
          title={collapsed ? 'New Chat' : undefined}
        >
          <MessageSquarePlus size={18} />
          {!collapsed && <span>New Chat</span>}
        </button>
      </div>

      {/* Chat History — hidden entirely while collapsed since the list needs labels to be useful */}
      {!collapsed && (
      <div className="flex-1 overflow-y-auto">
        <div className="px-3 py-2 flex items-center justify-between">
          <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-1">
            <History size={12} />
            Recent Chats
          </h2>
          {sessions.length > 0 && (
            <button
              onClick={clearAllSessions}
              className="text-gray-500 hover:text-red-400 transition-colors p-1"
              title="Clear all chat history"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        <nav className="px-2 space-y-1">
          {sessions.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">No chat history</p>
          ) : (
            paginatedSessions.map((session) => (
              <div
                key={session.id}
                className="relative group"
                onMouseEnter={() => setHoveredSession(session.id)}
                onMouseLeave={() => setHoveredSession(null)}
              >
                <button
                  onClick={() => {
                    loadSession(session.id);
                    // Navigate to chat route if not already there
                    if (window.location.hash !== '#/' && window.location.hash !== '#' && window.location.hash !== '') {
                      window.location.hash = '#/';
                    }
                  }}
                  className={`w-full text-left px-3 py-2 pr-8 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                    session.id === sessionId
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <ChevronRight size={14} className="flex-shrink-0" />
                  <span className="truncate">{session.title}</span>
                </button>
                {/* Delete button */}
                {hoveredSession === session.id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.id);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-600 text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete this chat"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))
          )}
        </nav>
        {/* Pagination controls */}
        {totalChatPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-3 py-2">
            <button
              onClick={() => setChatPage(Math.max(0, safePage - 1))}
              disabled={safePage <= 0}
              className="p-1 rounded hover:bg-gray-700 text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-gray-400">
              {safePage + 1}/{totalChatPages}
            </span>
            <button
              onClick={() => setChatPage(Math.min(totalChatPages - 1, safePage + 1))}
              disabled={safePage >= totalChatPages - 1}
              className="p-1 rounded hover:bg-gray-700 text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
      )}

      {/* Spacer so the footer sits at the bottom even when chat history is hidden */}
      {collapsed && <div className="flex-1" />}

      {/* Footer */}
      <div className={`border-t border-gray-700 space-y-1 ${collapsed ? 'p-2' : 'p-3'}`}>
        {/* User info — hidden while collapsed (no room for the email) */}
        {!collapsed && profile && (
          <div className="px-3 py-2 text-xs text-gray-400 truncate" title={profile.email || ''}>
            {profile.display_name || profile.email}
          </div>
        )}
        <a
          href="#/doc-processing"
          className={navLinkClass('#/doc-processing')}
          title={collapsed ? 'Doc Processing' : undefined}
        >
          <Shield size={16} />
          {!collapsed && <span>Doc Processing</span>}
        </a>
        <a
          href="#/issue-analysis"
          className={navLinkClass('#/issue-analysis')}
          title={collapsed ? 'Issue Analysis' : undefined}
        >
          <SearchCheck size={16} />
          {!collapsed && <span>Issue Analysis</span>}
        </a>
        <a
          href="#/cr-analysis"
          className={navLinkClass('#/cr-analysis')}
          title={collapsed ? 'CR Analysis' : undefined}
        >
          <FileSearch size={16} />
          {!collapsed && <span>CR Analysis</span>}
        </a>
        <a
          href="#/graph-viz"
          className={navLinkClass('#/graph-viz')}
          title={collapsed ? 'Graph Viz' : undefined}
        >
          <Network size={16} />
          {!collapsed && <span>Graph Viz</span>}
        </a>
        {/* Evaluation collapsible menu — admin-only (UI gate; backend
            endpoints remain callable). Collapsed sidebar: clicking the
            icon expands the sidebar AND opens the submenu so both Eval
            Results / Eval Review are reachable in one extra click. */}
        {isAdmin && (
        <div>
          <button
            onClick={() => {
              if (collapsed) {
                setSidebarCollapsed(false);
                setEvalMenuOpen(true);
              } else {
                setEvalMenuOpen(!evalMenuOpen);
              }
            }}
            className={`w-full flex items-center rounded-lg transition-colors text-sm ${
              collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-3 py-2'
            } ${
              activeRoute === '#/eval-results' || activeRoute === '#/evaluation'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:bg-gray-800'
            }`}
            title={collapsed ? 'Evaluation' : undefined}
          >
            <FlaskConical size={16} />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Evaluation</span>
                {evalMenuOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </>
            )}
          </button>
          {!collapsed && evalMenuOpen && (
            <div className="ml-4 space-y-0.5">
              <a href="#/eval-results" className={subNavLinkClass('#/eval-results')}>
                <BarChart3 size={14} />
                <span>Eval Results</span>
              </a>
              <a href="#/evaluation" className={subNavLinkClass('#/evaluation')}>
                <ClipboardCheck size={14} />
                <span>Eval Review</span>
              </a>
            </div>
          )}
        </div>
        )}
        {/* Settings — admin-only (UI gate; backend endpoints remain callable). */}
        {isAdmin && (
        <button
          onClick={() => setShowSettings(true)}
          className={`w-full flex items-center rounded-lg text-gray-400 hover:bg-gray-800 transition-colors text-sm ${
            collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-3 py-2'
          }`}
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={16} />
          {!collapsed && <span>Settings</span>}
        </button>
        )}
        {/* Security — admin-only */}
        {isAdmin && (
        <>
          <a
            href="#/admin/security"
            className={`w-full flex items-center rounded-lg text-gray-400 hover:bg-gray-800 transition-colors text-sm ${
              activeRoute === '#/admin/security' ? 'bg-gray-700 text-white' : ''
            } ${
              collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-3 py-2'
            }`}
            title={collapsed ? 'Security' : undefined}
          >
            <Key size={16} />
            {!collapsed && <span>Security</span>}
          </a>
          <div>
            <button
              onClick={() => {
                if (collapsed) {
                  setSidebarCollapsed(false);
                  setAdminMenuOpen(true);
                } else {
                  setAdminMenuOpen(!adminMenuOpen);
                }
              }}
              className={`w-full flex items-center rounded-lg transition-colors text-sm ${
                collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-3 py-2'
              } ${
                activeRoute === '#/admin/system-config' || activeRoute === '#/admin/usage-stats' || activeRoute === '#/ts-graph'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:bg-gray-800'
              }`}
              title={collapsed ? 'System Admin' : undefined}
            >
              <Sliders size={16} />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">System Admin</span>
                  {adminMenuOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </>
              )}
            </button>
            {!collapsed && adminMenuOpen && (
              <div className="ml-4 space-y-0.5">
                <a href="#/admin/system-config" className={subNavLinkClass('#/admin/system-config')}>
                  <Sliders size={14} />
                  <span>Configuration</span>
                </a>
                <a href="#/admin/usage-stats" className={subNavLinkClass('#/admin/usage-stats')}>
                  <BarChart3 size={14} />
                  <span>Usage Stats</span>
                </a>
                <a href="#/ts-graph" className={subNavLinkClass('#/ts-graph')}>
                  <GitBranch size={14} />
                  <span>Dependency Graph</span>
                </a>
              </div>
            )}
          </div>
        </>
        )}
        <button
          onClick={() => logout()}
          className={`w-full flex items-center rounded-lg text-gray-400 hover:bg-gray-800 hover:text-red-400 transition-colors text-sm ${
            collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-3 py-2'
          }`}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={16} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Settings Modal */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
}
